// src/pages/billing/BillingSettings.jsx
// Billing settings page for managing subscription via Stripe Portal

import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";

import { billingService } from "../../api/billingService";
import { useSubscription } from "../../context/SubscriptionContext";
import { SeatsProgressBar } from "../../components/SeatsBadge";
import UpgradeBanner from "../../components/UpgradeBanner";

const statusConfig = {
  active: {
    label: "Active",
    color: "success",
    icon: CheckCircleRoundedIcon,
  },
  trialing: {
    label: "Trial",
    color: "info",
    icon: ScheduleRoundedIcon,
  },
  trial: {
    label: "Trial",
    color: "info",
    icon: ScheduleRoundedIcon,
  },
  past_due: {
    label: "Past Due",
    color: "warning",
    icon: WarningAmberRoundedIcon,
  },
  canceled: {
    label: "Canceled",
    color: "error",
    icon: WarningAmberRoundedIcon,
  },
};

function formatCurrency(amount, currency) {
  if (amount == null) return null;
  const value = amount / 100;
  const curr = (currency || "usd").toUpperCase();
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: curr,
    }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
}

export default function BillingSettings() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { status, seats, loading: statusLoading } = useSubscription();
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState("");
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetchDetails() {
      try {
        const data = await billingService.getSubscriptionDetails();
        if (!cancelled) setDetails(data);
      } catch (err) {
        console.error("Failed to load subscription details:", err);
      } finally {
        if (!cancelled) setDetailsLoading(false);
      }
    }
    fetchDetails();
    return () => { cancelled = true; };
  }, []);

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    setError("");

    try {
      const { url } = await billingService.openPortal();
      if (url) {
        window.location.href = url;
      } else {
        throw new Error("No portal URL received");
      }
    } catch (err) {
      console.error("Portal error:", err);
      setError(err.message || "Failed to open billing portal");
    } finally {
      setPortalLoading(false);
    }
  };

  if (statusLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const subscriptionStatus = status?.subscription_status || "none";
  const statusInfo = statusConfig[subscriptionStatus] || {
    label: "No Subscription",
    color: "default",
  };
  const StatusIcon = statusInfo.icon;

  const trialEndsAt = status?.trial_ends_at
    ? new Date(status.trial_ends_at).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  // Build price display from Stripe data (details) or fall back to plan name
  const planName = status?.subscription_plan;
  const displayName = planName
    ? planName
        .replace(/^agentblayna[_\s]*/i, "")
        .replace(/[_-]/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase()) || planName
    : null;

  const priceDisplay = details?.plan_amount != null
    ? `${formatCurrency(details.plan_amount, details.plan_currency)}/${details.plan_interval || "mo"}`
    : null;

  const renewalDate = details?.current_period_end
    ? new Date(details.current_period_end).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  const invoices = details?.invoices || [];

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", p: 3 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Billing & Subscription
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage your subscription, team seats, and billing details.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Upgrade Banner when seats are full */}
      {seats && !seats.can_add_members && <UpgradeBanner />}

      <Grid container spacing={3}>
        {/* Current Plan Card */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2, height: "100%" }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6" fontWeight={600}>
                Current Plan
              </Typography>
              <Chip
                icon={StatusIcon ? <StatusIcon sx={{ fontSize: 16 }} /> : undefined}
                label={statusInfo.label}
                color={statusInfo.color}
                size="small"
              />
            </Stack>

            {displayName ? (
              <>
                <Typography variant="h4" fontWeight={700} color="primary">
                  {displayName}
                </Typography>
                {priceDisplay ? (
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                    {priceDisplay}
                  </Typography>
                ) : detailsLoading ? (
                  <CircularProgress size={16} sx={{ mt: 1 }} />
                ) : null}
                {renewalDate && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {details?.cancel_at_period_end
                      ? `Cancels on ${renewalDate}`
                      : `Renews on ${renewalDate}`}
                  </Typography>
                )}

                {details?.cancel_at_period_end && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Your subscription will cancel at the end of the current billing
                    period ({renewalDate}). You can resubscribe from the billing portal.
                  </Alert>
                )}
              </>
            ) : status?.trial_active ? (
              <>
                <Typography variant="h4" fontWeight={700} color="info.main">
                  Free Trial
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {trialEndsAt ? `Ends on ${trialEndsAt}` : "Trial active"}
                </Typography>
              </>
            ) : (
              <Typography variant="body1" color="text.secondary">
                No active subscription
              </Typography>
            )}

            {status?.trial_active && trialEndsAt && !status?.has_subscription && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Your trial ends on {trialEndsAt}. Subscribe now to keep access.
              </Alert>
            )}

            {subscriptionStatus === "past_due" && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Payment past due. Please update your payment method.
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Seats Card */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2, height: "100%" }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 2 }}
            >
              <PeopleRoundedIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Team Seats
              </Typography>
            </Stack>

            {seats ? (
              <>
                <Box sx={{ mb: 2 }}>
                  <SeatsProgressBar />
                </Box>

                <Typography variant="body2" color="text.secondary">
                  {seats.seats_available > 0
                    ? `${seats.seats_available} seat${seats.seats_available > 1 ? "s" : ""} available`
                    : "No seats available"}
                </Typography>

                <Button
                  variant="text"
                  size="small"
                  onClick={() => navigate("/app/team")}
                  sx={{ mt: 2, p: 0 }}
                >
                  Manage Team Members
                </Button>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Loading seat information...
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Invoices Section */}
      {invoices.length > 0 && (
        <>
          <Divider sx={{ my: 4 }} />

          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <ReceiptLongRoundedIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Recent Invoices
            </Typography>
          </Stack>

          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Receipt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((inv, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      {inv.date
                        ? new Date(inv.date).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {inv.amount != null
                        ? formatCurrency(inv.amount, inv.currency)
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={inv.status || "unknown"}
                        size="small"
                        color={inv.status === "paid" ? "success" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {inv.url ? (
                        <Link
                          href={inv.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          variant="body2"
                        >
                          View
                        </Link>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Divider sx={{ my: 4 }} />

      {/* Quick Actions */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Quick Actions
      </Typography>

      <Grid container spacing={2}>
        {/* Manage Subscription */}
        <Grid item xs={12} sm={6}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 2,
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                borderColor: "primary.main",
              },
              border: "1px solid",
              borderColor: "divider",
            }}
            onClick={status?.has_subscription ? handleOpenPortal : () => navigate("/pricing")}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CreditCardRoundedIcon color="primary" />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {status?.has_subscription ? "Manage Subscription" : "Subscribe Now"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {status?.has_subscription
                    ? "Update payment method or cancel"
                    : "Choose a plan to get started"}
                </Typography>
              </Box>
              {portalLoading ? (
                <CircularProgress size={20} />
              ) : (
                <OpenInNewRoundedIcon color="action" />
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Upgrade Plan */}
        {status?.has_subscription && (
          <Grid item xs={12} sm={6}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 2,
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  bgcolor: alpha(theme.palette.success.main, 0.04),
                  borderColor: "success.main",
                },
                border: "1px solid",
                borderColor: "divider",
              }}
              onClick={() => navigate("/pricing")}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <RocketLaunchRoundedIcon color="success" />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Upgrade Plan
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Get more seats and features
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Help */}
      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Need help? Contact us at{" "}
          <a
            href="mailto:support@agentblayna.com"
            style={{ color: "inherit" }}
          >
            support@agentblayna.com
          </a>
        </Typography>
      </Box>
    </Box>
  );
}
